import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

/** Re-check at most this often if the tab is left open (admin toggles still land). */
const CACHE_TTL_MS = 5 * 60 * 1000;

const EMPTY = {
  enabled: false,
  popupEnabled: false,
  message: "",
  launchAt: null,
};

let cache = null;
let inflight = null;
const listeners = new Set();

function snapshot() {
  if (!cache) return { ...EMPTY };
  return {
    enabled: cache.enabled,
    popupEnabled: cache.popupEnabled,
    message: cache.message,
    launchAt: cache.launchAt,
  };
}

function notify() {
  const snap = snapshot();
  listeners.forEach((fn) => fn(snap));
}

function applyPayload(data) {
  cache = {
    enabled: Boolean(data?.enabled),
    popupEnabled: Boolean(data?.popup_enabled),
    message: data?.message || "",
    launchAt: data?.launch_at || null,
    fetchedAt: Date.now(),
  };
  notify();
  return cache;
}

/** After admin saves settings, keep the public SPA cache in sync (no extra GET). */
export function syncLaunchGateFromAdmin(data) {
  applyPayload(data);
}

/**
 * One in-flight GET + module cache so StrictMode, remounts, and extra hook
 * instances cannot spam /api/launch-gate.
 */
export function fetchLaunchGate({ force = false } = {}) {
  const fresh = cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS;
  if (!force && fresh) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = api
    .get("/api/launch-gate")
    .then(({ data }) => applyPayload(data))
    .catch(() => {
      if (!cache) applyPayload(EMPTY);
      return cache;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/**
 * Fetches public launch-gate status once per session (and at most every 5 min).
 * No interval and no focus/visibility polling — those were flooding the API.
 */
export function useLaunchGate() {
  const [state, setState] = useState(() => ({
    ...snapshot(),
    loading: !cache,
  }));

  useEffect(() => {
    let cancelled = false;
    const onUpdate = (snap) => {
      if (!cancelled) setState({ ...snap, loading: false });
    };
    listeners.add(onUpdate);
    fetchLaunchGate().then(() => {
      if (!cancelled) setState({ ...snapshot(), loading: false });
    });
    return () => {
      cancelled = true;
      listeners.delete(onUpdate);
    };
  }, []);

  const refresh = useCallback(() => fetchLaunchGate({ force: true }), []);

  return { ...state, refresh };
}
